import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { convertISOToArgentine, convertArgentineToISO } from '../utils/formatters';

interface ArgentineDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const ArgentineDatePicker: React.FC<ArgentineDatePickerProps> = ({
  value,
  onChange,
  placeholder = "dd-MM-YYYY",
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayValue, setDisplayValue] = useState(value);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Initialize selected date from value
  useEffect(() => {
    setDisplayValue(value);
    if (value) {
      try {
        const isoDate = convertArgentineToISO(value);
        if (isoDate) {
          const date = new Date(isoDate);
          if (!isNaN(date.getTime())) {
            setSelectedDate(date);
            setCurrentDate(date);
          }
        }
      } catch (error) {
        console.error('Error parsing date:', error);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        // Call onChange with the final selected value when closing
        if (displayValue !== value) {
          onChange(displayValue);
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, displayValue, value, onChange]);

  // Handle escape key to close picker
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Call onChange with the final selected value when closing
        if (displayValue !== value) {
          onChange(displayValue);
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, displayValue, value, onChange]);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    handleDateSelect(today);
    // Don't close the picker automatically
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const argentineDate = convertISOToArgentine(date.toISOString().split('T')[0]);
    setDisplayValue(argentineDate);
    // Don't close the picker automatically - let user close it manually
    // setIsOpen(false);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 w-8"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isTodayDate = isToday(date);
      const isSelectedDate = isSelected(date);
      const isCurrentMonthDate = isCurrentMonth(date);

      days.push(
        <button
          key={day}
          onClick={(e) => {
            e.stopPropagation();
            handleDateSelect(date);
          }}
          className={`
            h-8 w-8 rounded-full text-sm font-medium transition-colors
            ${isSelectedDate 
              ? 'bg-primary-600 text-white' 
              : isTodayDate 
                ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' 
                : isCurrentMonthDate 
                  ? 'text-gray-900 hover:bg-gray-100' 
                  : 'text-gray-400 hover:bg-gray-50'
            }
            ${!isCurrentMonthDate ? 'opacity-50' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div ref={pickerRef} className="relative">
      {/* Input Field */}
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={(e) => {
            setDisplayValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          className={`w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${className}`}
          disabled={disabled}
          pattern="\d{2}-\d{2}-\d{4}"
          title="Formato: dd-MM-YYYY"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            !disabled && setIsOpen(!isOpen);
          }}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={disabled}
        >
          <Calendar className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[280px]"
          >
            {/* Calendar Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPreviousMonth();
                  }}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNextMonth();
                  }}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              
              <div className="flex space-x-2">
                {/* Today Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToToday();
                  }}
                  className="flex-1 py-2 px-3 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 transition-colors"
                >
                  Hoy
                </button>
                
                {/* Close Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Call onChange with the final selected value when closing
                    if (displayValue !== value) {
                      onChange(displayValue);
                    }
                    setIsOpen(false);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-4">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="h-8 w-8 flex items-center justify-center text-xs font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArgentineDatePicker;
